import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}


// Ranges that use daily bars — weekends/holidays are gap-filled so the chart line
// is continuous and zooming in doesn't reveal trading-day-only gaps.
const DAILY_BAR_RANGES = new Set(['30d', '90d', '180d', 'ytd', '1y', '3y', '5y', 'all', 'since_first_buy']);

/**
 * Forward-fill missing calendar days (weekends / holidays) with the last known
 * closing price. Input/output items have the shape { rawDate, parsedDate, price }.
 */
function fillWeekendGaps(items) {
  if (!items || items.length < 2) return items;
  const filled = [];
  for (let i = 0; i < items.length; i++) {
    const curr = items[i];
    filled.push(curr);
    if (i < items.length - 1) {
      const cursor = new Date(curr.parsedDate);
      cursor.setDate(cursor.getDate() + 1);
      const next = items[i + 1].parsedDate;
      while (cursor < next) {
        const iso = cursor.toISOString().split('T')[0];
        filled.push({ rawDate: iso, parsedDate: new Date(cursor), price: curr.price });
        cursor.setDate(cursor.getDate() + 1);
      }
    }
  }
  return filled;
}


function HoldingChart({
  ticker,
  displayName,
  loading = false,
  points = [],
  range = 'all',
  onChangeRange = () => {},
  instrumentCurrency = 'USD',
  transactions = [],
  firstBuyDate = null,
  lineColorOverride = null,
  hideHeader = false, // when true, skip the title/ticker/price/change row at the top
}) {
  const isMobile = useIsMobile();
  const [hoverIndex, setHoverIndex] = useState(null);
  const [showAvgBuy, setShowAvgBuy] = useState(true);
  const [showMarkers, setShowMarkers] = useState(true);
  const [showRangeDropdown, setShowRangeDropdown] = useState(false);
  const [showVisibleDropdown, setShowVisibleDropdown] = useState(false);
  const rangeDropdownRef = useRef(null);
  const visibleDropdownRef = useRef(null);

  // Zoom / pan state — indices into chartData (the base filtered data)
  const [viewStartIdx, setViewStartIdx] = useState(0);
  const [viewEndIdx, setViewEndIdx] = useState(null); // null = chartData.length - 1
  const [isDragging, setIsDragging] = useState(false);

  const svgRef = useRef(null);
  const dragRef = useRef(null); // { startX, startViewStart, startViewEnd }
  const isDraggingRef = useRef(false);
  // Stable ref so wheel handler never needs to be re-registered
  const wheelStateRef = useRef({});

  // ── Parse and sort all price points ──────────────────────────────────────
  const allChartData = useMemo(() => {
    return [...(Array.isArray(points) ? points : [])]
      .map((item) => {
        const rawDate = item?.date || item?.Date || '';
        const parsedDate = rawDate ? new Date(rawDate) : null;
        return {
          rawDate,
          parsedDate,
          price: Number(item?.price || item?.Price || item?.close || item?.Close || 0),
        };
      })
      .filter(
        (item) =>
          item.rawDate &&
          item.parsedDate &&
          !Number.isNaN(item.parsedDate.getTime()) &&
          item.price > 0
      )
      .sort((a, b) => a.parsedDate - b.parsedDate);
  }, [points]);

  // "since_first_buy" clips from firstBuyDate; otherwise full data.
  // Daily-bar ranges get weekend/holiday gap-fill so the line is continuous.
  const chartData = useMemo(() => {
    let data = allChartData;
    if (range === 'since_first_buy' && firstBuyDate) {
      const cutoff = new Date(firstBuyDate);
      cutoff.setDate(cutoff.getDate() - 1);
      data = allChartData.filter((p) => p.parsedDate >= cutoff);
    }
    return DAILY_BAR_RANGES.has(range) ? fillWeekendGaps(data) : data;
  }, [allChartData, range, firstBuyDate]);

  // Reset zoom/pan when range or data changes
  useEffect(() => {
    setViewStartIdx(0);
    setViewEndIdx(null);
    setHoverIndex(null);
  }, [range, allChartData]);

  // Effective view window
  const effEnd = viewEndIdx !== null ? Math.min(viewEndIdx, chartData.length - 1) : chartData.length - 1;
  const effStart = Math.min(viewStartIdx, Math.max(0, effEnd));

  // The slice of data actually displayed
  const displayData = useMemo(() => {
    if (!chartData.length) return chartData;
    return chartData.slice(effStart, effEnd + 1);
  }, [chartData, effStart, effEnd]);

  // ── Dynamic average buy price series ─────────────────────────────────────
  const avgBuySeriesFull = useMemo(() => {
    if (!chartData.length) return [];

    // Include both buys and sells so avg resets when position is fully closed
    const events = (Array.isArray(transactions) ? transactions : [])
      .filter((tx) => tx.transactionType === 0 || tx.transactionType === 1)
      .map((tx) => ({
        dateStr: (tx.occurredAt || '').split('T')[0],
        type: Number(tx.transactionType), // 0=buy, 1=sell
        qty: Number(tx.quantity || 0),
        price: Number(tx.pricePerUnit || 0),
      }))
      .filter((e) => e.qty > 0 && e.dateStr)
      .sort((a, b) => a.dateStr.localeCompare(b.dateStr) || a.type - b.type);

    if (!events.length) return [];

    const DUST = 1e-8;
    let totalQty = 0, totalCost = 0, evtIdx = 0;
    return chartData.map((item) => {
      const datePart = item.rawDate.split('T')[0];
      while (evtIdx < events.length && events[evtIdx].dateStr <= datePart) {
        const e = events[evtIdx];
        if (e.type === 0) {
          // BUY — accumulate
          totalCost += e.qty * e.price;
          totalQty += e.qty;
        } else {
          // SELL — remove cost basis (average cost method)
          const avgCost = totalQty > 0 ? totalCost / totalQty : 0;
          totalCost = Math.max(0, totalCost - e.qty * avgCost);
          totalQty = Math.max(0, totalQty - e.qty);
          // Full close — hard reset so rounding dust doesn't linger
          if (totalQty < DUST) { totalQty = 0; totalCost = 0; }
        }
        evtIdx++;
      }
      return totalQty > 0 ? totalCost / totalQty : null;
    });
  }, [chartData, transactions]);

  // Sliced to display window
  const avgBuySeries = useMemo(
    () => (avgBuySeriesFull.length ? avgBuySeriesFull.slice(effStart, effEnd + 1) : avgBuySeriesFull),
    [avgBuySeriesFull, effStart, effEnd]
  );

  const currentAvgBuy = avgBuySeries.length
    ? [...avgBuySeries].reverse().find((v) => v !== null) ?? null
    : null;

  // ── Buy/sell markers (nearest-date, deduplicated) ─────────────────────────
  const txMarkersFull = useMemo(() => {
    if (!chartData.length) return [];
    const buySells = (Array.isArray(transactions) ? transactions : []).filter(
      (tx) => tx.transactionType === 0 || tx.transactionType === 1
    );
    const grouped = new Map();
    buySells.forEach((tx) => {
      const txDate = new Date((tx.occurredAt || '').split('T')[0]);
      if (Number.isNaN(txDate.getTime())) return;
      let bestIdx = -1, bestDiff = Infinity;
      chartData.forEach((p, i) => {
        if (!p.parsedDate) return;
        const diff = Math.abs(p.parsedDate.getTime() - txDate.getTime());
        if (diff < bestDiff && diff <= 7 * 86_400_000) { bestDiff = diff; bestIdx = i; }
      });
      if (bestIdx === -1) return;
      const key = `${bestIdx}-${tx.transactionType}`;
      if (!grouped.has(key)) grouped.set(key, { index: bestIdx, type: tx.transactionType === 0 ? 'buy' : 'sell', count: 0, qty: 0 });
      const entry = grouped.get(key);
      entry.count += 1;
      entry.qty += Number(tx.quantity || 0);
    });
    return Array.from(grouped.values());
  }, [chartData, transactions]);

  // Filter and remap markers to the display window
  const txMarkers = useMemo(
    () =>
      txMarkersFull
        .filter((m) => m.index >= effStart && m.index <= effEnd)
        .map((m) => ({ ...m, index: m.index - effStart })),
    [txMarkersFull, effStart, effEnd]
  );

  // ── SVG layout ────────────────────────────────────────────────────────────
  const width = 1000;
  const height = 340;
  const paddingLeft = 0;
  const paddingRight = 0;
  const paddingTop = 14;
  const paddingBottom = 20;

  const allValues = [
    ...displayData.map((p) => p.price),
    ...avgBuySeries.filter((v) => v !== null),
  ];

  const minValueRaw = allValues.length ? Math.min(...allValues) : 0;
  const maxValueRaw = allValues.length ? Math.max(...allValues) : 1;
  const dataRange = Math.abs(maxValueRaw - minValueRaw);
  const valuePadding = Math.max(dataRange * 0.08, maxValueRaw * 0.02, 0.01);

  const normalizedMin = minValueRaw === maxValueRaw ? minValueRaw - 1 : minValueRaw - valuePadding;
  const normalizedMax = minValueRaw === maxValueRaw ? maxValueRaw + 1 : maxValueRaw + valuePadding;

  const getX = (index) => {
    if (displayData.length <= 1) return 0;
    return (index / (displayData.length - 1)) * width;
  };
  const getY = (value) => {
    const usableHeight = height - paddingTop - paddingBottom;
    const ratio = (value - normalizedMin) / (normalizedMax - normalizedMin || 1);
    return height - paddingBottom - ratio * usableHeight;
  };

  const firstPrice = displayData[0]?.price ?? 0;
  const lastPrice = displayData[displayData.length - 1]?.price ?? 0;
  const priceChange = lastPrice - firstPrice;
  const priceChangePct = firstPrice > 0 ? (priceChange / firstPrice) * 100 : 0;

  const lineColor = lineColorOverride || (priceChange >= 0 ? '#8b7cff' : '#ef4444');
  const plColor = priceChange > 0 ? 'var(--success)' : priceChange < 0 ? 'var(--danger)' : 'var(--text-primary)';
  const avgBuyColor = '#f59e0b';

  const linePath = displayData
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${getX(index)} ${getY(point.price)}`)
    .join(' ');

  const areaPath = displayData.length
    ? `${linePath} L ${width} ${height} L 0 ${height} Z`
    : '';

  // Stepping avg buy path
  const avgBuyPath = useMemo(() => {
    const parts = [];
    avgBuySeries.forEach((val, i) => {
      if (val === null) return;
      const x = getX(i);
      const y = getY(val);
      if (parts.length === 0) parts.push(`M ${x} ${y}`);
      else parts.push(`L ${x} ${y}`);
    });
    return parts.join(' ');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avgBuySeries, displayData.length, normalizedMin, normalizedMax]);

  const hoverPoint =
    hoverIndex != null && hoverIndex >= 0 && hoverIndex < displayData.length
      ? displayData[hoverIndex]
      : null;
  const tooltipX = hoverPoint ? getX(hoverIndex) : 0;
  const tooltipY = hoverPoint ? getY(hoverPoint.price) : 0;
  const hoverAvgBuy = hoverIndex != null ? (avgBuySeries[hoverIndex] ?? null) : null;

  const yTicks = buildYTicks(normalizedMin, normalizedMax);
  const xTickIndexes = buildXTickIndexes(displayData.length);

  // Compute visible date span (days) so x-axis labels pick the right granularity
  const visibleSpanDays = useMemo(() => {
    if (displayData.length < 2) return 0;
    const first = displayData[0]?.parsedDate;
    const last  = displayData[displayData.length - 1]?.parsedDate;
    if (!first || !last) return 0;
    return (last - first) / 86_400_000;
  }, [displayData]);

  // ── Zoom / Pan event handlers ─────────────────────────────────────────────
  // Keep latest values in a ref so the wheel handler is stable
  wheelStateRef.current = { chartData, effStart, effEnd, range, onChangeRange };

  // Stable wheel handler — reads all live values from wheelStateRef
  const handleWheelStable = useCallback((e) => {
    e.preventDefault();
    const { chartData: cd, effStart: es, effEnd: ee } = wheelStateRef.current;
    if (!cd.length) return;

    const svgEl = svgRef.current;
    if (!svgEl) return;
    const rect = svgEl.getBoundingClientRect();
    const svgW = rect.width || 1;
    const chartAreaLeft = (paddingLeft / width) * svgW;
    const chartAreaRight = ((width - paddingRight) / width) * svgW;
    const mouseInChart = e.clientX - rect.left;
    const chartFrac = Math.max(0, Math.min(1, (mouseInChart - chartAreaLeft) / (chartAreaRight - chartAreaLeft)));

    const currentSpan = ee - es + 1;
    const isZoomOut = e.deltaY > 0;
    const factor = isZoomOut ? 1.18 : (1 / 1.18);
    const newSpan = Math.max(10, Math.min(cd.length, Math.round(currentSpan * factor)));

    const anchorIdx = es + chartFrac * currentSpan;
    let newStart = Math.round(anchorIdx - chartFrac * newSpan);
    let newEnd = newStart + newSpan - 1;

    if (newStart < 0) { newEnd = Math.min(cd.length - 1, newEnd - newStart); newStart = 0; }
    if (newEnd >= cd.length) { newStart = Math.max(0, newStart - (newEnd - cd.length + 1)); newEnd = cd.length - 1; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(cd.length - 1, newEnd);

    setViewStartIdx(newStart);
    setViewEndIdx(newEnd);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable — reads from wheelStateRef

  // SVG ref callback — registers wheel listener (passive:false) whenever SVG mounts/unmounts
  const svgCallbackRef = useCallback((el) => {
    if (svgRef.current) {
      svgRef.current.removeEventListener('wheel', handleWheelStable);
    }
    svgRef.current = el;
    if (el) {
      el.addEventListener('wheel', handleWheelStable, { passive: false });
    }
  }, [handleWheelStable]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    isDraggingRef.current = true;
    setIsDragging(true);
    dragRef.current = { startX: e.clientX, startViewStart: effStart, startViewEnd: effEnd };
  }, [effStart, effEnd]);

  const handleMouseMoveDrag = useCallback((e) => {
    if (!isDraggingRef.current || !dragRef.current || !chartData.length) return;
    const rect = svgRef.current?.getBoundingClientRect();
    const svgW = rect?.width || 1;
    const chartAreaW = ((width - paddingLeft - paddingRight) / width) * svgW;
    const dx = dragRef.current.startX - e.clientX;
    const span = dragRef.current.startViewEnd - dragRef.current.startViewStart + 1;
    const deltaIdx = Math.round((dx / chartAreaW) * span);

    let newStart = dragRef.current.startViewStart + deltaIdx;
    let newEnd = dragRef.current.startViewEnd + deltaIdx;

    if (newStart < 0) { newEnd -= newStart; newStart = 0; }
    if (newEnd >= chartData.length) { newStart -= (newEnd - chartData.length + 1); newEnd = chartData.length - 1; }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(chartData.length - 1, newEnd);

    setViewStartIdx(newStart);
    setViewEndIdx(newEnd);
  }, [chartData.length]);

  const handleMouseUpGlobal = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    dragRef.current = null;
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMoveDrag);
    window.addEventListener('mouseup', handleMouseUpGlobal);
    return () => {
      window.removeEventListener('mousemove', handleMouseMoveDrag);
      window.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  }, [handleMouseMoveDrag, handleMouseUpGlobal]);

  // Close mobile dropdowns on outside click
  useEffect(() => {
    if (!showRangeDropdown && !showVisibleDropdown) return;
    const handler = (e) => {
      if (rangeDropdownRef.current && !rangeDropdownRef.current.contains(e.target)) {
        setShowRangeDropdown(false);
      }
      if (visibleDropdownRef.current && !visibleDropdownRef.current.contains(e.target)) {
        setShowVisibleDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showRangeDropdown, showVisibleDropdown]);

  // ── Range selector ────────────────────────────────────────────────────────
  const rangeOptions = [
    { value: '1d', label: '1D' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '1M' },
    { value: '90d', label: '3M' },
    { value: '180d', label: '6M' },
    { value: 'ytd', label: 'YTD' },
    { value: '1y', label: '1Y' },
    { value: '3y', label: '3Y' },
    { value: '5y', label: '5Y' },
    { value: 'all', label: 'ALL' },
    ...(firstBuyDate ? [{ value: 'since_first_buy', label: 'Since buy' }] : []),
  ];

  return (
    <section style={cardStyle}>
      {!hideHeader && (
        <div style={headerStyle}>
          <div>
            <h3 style={titleStyle}>{displayName || ticker}</h3>
            <div style={subtitleStyle}>{ticker} · {instrumentCurrency}</div>
          </div>

          <div style={rightHeaderStyle}>
            {lastPrice > 0 && (
              <strong style={valueValueStyle}>{formatCurrency(lastPrice, instrumentCurrency)}</strong>
            )}
            {firstPrice > 0 && (
              <div style={{ display: 'grid', gap: '2px', textAlign: 'right' }}>
                <strong style={{ ...plValueStyle, color: plColor }}>
                  {priceChangePct >= 0 ? '+' : ''}{priceChangePct.toFixed(2)}%
                </strong>
                <span style={{ ...plSubValueStyle, color: plColor }}>
                  {priceChange >= 0 ? '+' : ''}{formatCurrency(priceChange, instrumentCurrency)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile controls row */}
      {isMobile ? (
        <div style={mobileControlsRowStyle}>
          {/* Visible button (left) */}
          <div ref={visibleDropdownRef} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setShowVisibleDropdown(v => !v)} style={mobileDropdownBtnStyle}>
              <span>Visible</span>
              <span style={{ fontSize: '10px' }}>▾</span>
            </button>
            {showVisibleDropdown && (
              <div style={mobileDropdownPanelStyle}>
                {currentAvgBuy !== null && (
                  <button type="button" onClick={() => setShowAvgBuy(v => !v)} style={dropdownItemStyle}>
                    <span style={{ ...legendLineStyle, borderColor: avgBuyColor, marginRight: 6 }} />
                    Avg buy {showAvgBuy ? '✓' : ''}
                  </button>
                )}
                {txMarkersFull.length > 0 && (
                  <button type="button" onClick={() => setShowMarkers(v => !v)} style={dropdownItemStyle}>
                    Buy/sell markers {showMarkers ? '✓' : ''}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Range button (right) */}
          <div ref={rangeDropdownRef} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setShowRangeDropdown(v => !v)} style={mobileDropdownBtnStyle}>
              <span>{rangeOptions.find(r => r.value === range)?.label || range}</span>
              <span style={{ fontSize: '10px' }}>▾</span>
            </button>
            {showRangeDropdown && (
              <div style={{ ...mobileDropdownPanelStyle, right: 0, left: 'auto' }}>
                {rangeOptions.map(item => (
                  <button key={item.value} type="button"
                    onClick={() => { onChangeRange(item.value); setShowRangeDropdown(false); }}
                    style={{ ...dropdownItemStyle, ...(range === item.value ? dropdownItemActiveStyle : {}) }}>
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={rangeBarStyle}>
          {rangeOptions.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => onChangeRange(item.value)}
              style={range === item.value ? activeRangeButtonStyle : rangeButtonStyle}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {loading && chartData.length === 0 ? (
        <p style={emptyTextStyle}>Loading chart...</p>
      ) : chartData.length === 0 ? (
        range === '1d' ? (
          <div style={intradayEmptyStyle}>
            <span>📅 No intraday data — market may be closed (weekend or holiday).</span>
            <button type="button" style={intradayFallbackBtnStyle} onClick={() => onChangeRange('7d')}>
              Show last 7 days
            </button>
          </div>
        ) : (
          <p style={emptyTextStyle}>No price data available for this ticker.</p>
        )
      ) : (
        <>
          {/* Legend — only shown when there's something to legend besides Price
              (avg buy line, buy/sell markers). For standalone instrument detail
              pages with no transactions the legend would just say "Price",
              which is redundant with the chart itself, so we skip it. */}
          {(currentAvgBuy !== null || txMarkersFull.some((m) => m.type === 'buy' || m.type === 'sell')) && (
          <div style={legendStyle}>
            <div style={legendItemStyle}>
              <span style={{ ...legendSwatchStyle, background: lineColor }} />
              <span>Price</span>
            </div>
            {currentAvgBuy !== null && (
              <button
                type="button"
                onClick={() => setShowAvgBuy((v) => !v)}
                style={{ ...legendToggleStyle, opacity: showAvgBuy ? 1 : 0.4, textDecoration: showAvgBuy ? 'none' : 'line-through' }}
              >
                <span style={{ ...legendLineStyle, borderColor: avgBuyColor }} />
                <span style={{ color: avgBuyColor }}>Avg buy {formatCurrency(currentAvgBuy, instrumentCurrency)}</span>
              </button>
            )}
            {txMarkersFull.some((m) => m.type === 'buy') && (
              <button
                type="button"
                onClick={() => setShowMarkers((v) => !v)}
                style={{ ...legendToggleStyle, opacity: showMarkers ? 1 : 0.4, textDecoration: showMarkers ? 'none' : 'line-through' }}
              >
                <span style={buyLineLegendStyle} />
                <span style={{ color: 'var(--success)' }}>Buy</span>
              </button>
            )}
            {txMarkersFull.some((m) => m.type === 'sell') && (
              <button
                type="button"
                onClick={() => setShowMarkers((v) => !v)}
                style={{ ...legendToggleStyle, opacity: showMarkers ? 1 : 0.4, textDecoration: showMarkers ? 'none' : 'line-through' }}
              >
                <span style={{ ...buyLineLegendStyle, borderColor: '#ef4444' }} />
                <span style={{ color: '#ef4444' }}>Sell</span>
              </button>
            )}
          </div>
          )}

          <div style={chartWrapperStyle}>
            {/* Y-axis labels — overlaid on right */}
            {yTicks.map((tick, i) => (
              <div key={`yl-${i}`} style={{
                position: 'absolute',
                right: '8px',
                top: `${getY(tick)}px`,
                transform: 'translateY(-50%)',
                fontSize: '11px',
                color: 'var(--text-secondary)',
                fontWeight: 600,
                pointerEvents: 'none',
                lineHeight: 1,
                zIndex: 2,
              }}>
                {formatAxisNumber(tick, instrumentCurrency)}
              </div>
            ))}

            {/* X-axis date labels — overlaid at bottom */}
            {xTickIndexes.map((idx, i) => {
              const isFirst = i === 0;
              const isLast = i === xTickIndexes.length - 1;
              const pct = displayData.length <= 1 ? 0 : (idx / (displayData.length - 1)) * 100;
              return (
                <div key={`xl-${idx}`} style={{
                  position: 'absolute',
                  bottom: '6px',
                  fontSize: '11px',
                  color: 'var(--text-secondary)',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  zIndex: 2,
                  ...(isFirst
                    ? { left: '8px' }
                    : isLast
                    ? { right: '8px' }
                    : { left: `${pct}%`, transform: 'translateX(-50%)' }),
                }}>
                  {formatXLabel(displayData[idx]?.parsedDate, range, visibleSpanDays)}
                </div>
              );
            })}

            <svg
              ref={svgCallbackRef}
              viewBox={`0 0 ${width} ${height}`}
              style={{ ...svgStyle, cursor: isDragging ? 'grabbing' : 'grab' }}
              preserveAspectRatio="none"
              onMouseLeave={() => { if (!isDraggingRef.current) setHoverIndex(null); }}
              onMouseDown={handleMouseDown}
            >
              <defs>
                <linearGradient id="holdingAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={lineColor} stopOpacity="0.30" />
                  <stop offset="100%" stopColor={lineColor} stopOpacity="0.05" />
                </linearGradient>
              </defs>

              {/* Y grid lines only */}
              {yTicks.map((tick, index) => (
                <line key={index} x1={0} y1={getY(tick)} x2={width} y2={getY(tick)} stroke="var(--border)" strokeWidth="1" strokeDasharray="4 4" opacity="0.5" />
              ))}

              {/* Area fill */}
              {areaPath ? <path d={areaPath} fill="url(#holdingAreaGradient)" /> : null}

              {/* Avg buy stepping line */}
              {showAvgBuy && avgBuyPath && (
                <path d={avgBuyPath} fill="none" stroke={avgBuyColor} strokeWidth="2" strokeDasharray="8 5" strokeLinecap="round" opacity="0.9" />
              )}

              {/* Price line */}
              {linePath && (
                <path d={linePath} fill="none" stroke={lineColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.10))' }} />
              )}


              {/* Buy/sell markers — vertical dashed lines */}
              {showMarkers && txMarkers.map((marker, i) => {
                const x = getX(marker.index);
                const isBuy = marker.type === 'buy';
                const color = isBuy ? 'var(--success)' : '#ef4444';
                const countLabel = marker.count > 1 ? String(marker.count) : null;
                return (
                  <g key={i}>
                    <line x1={x} y1={paddingTop} x2={x} y2={height - paddingBottom} stroke={color} strokeWidth="1.5" strokeDasharray="5 4" opacity="0.5" />
                    {countLabel && (
                      <>
                        <circle cx={x} cy={paddingTop + 10} r="9" fill={color} opacity="0.9" />
                        <text x={x} y={paddingTop + 14} textAnchor="middle" style={{ fontSize: '10px', fill: '#fff', fontWeight: 800 }}>{countLabel}</text>
                      </>
                    )}
                  </g>
                );
              })}

              {/* Hover line + dot */}
              {hoverPoint && (
                <>
                  <line x1={tooltipX} y1={paddingTop} x2={tooltipX} y2={height - paddingBottom} stroke={lineColor} strokeWidth="1.5" strokeDasharray="5 5" opacity="0.7" />
                  <circle cx={tooltipX} cy={tooltipY} r="6" fill="#fff" stroke={lineColor} strokeWidth="3" />
                  <circle cx={tooltipX} cy={tooltipY} r="3.5" fill={lineColor} />
                </>
              )}

              {/* Hover hit areas */}
              {displayData.map((point, index) => {
                const x = getX(index);
                const leftX = index === 0 ? x : (getX(index - 1) + x) / 2;
                const rightX = index === displayData.length - 1 ? width - paddingRight : (x + getX(index + 1)) / 2;
                return (
                  <rect
                    key={point.rawDate + '-' + index}
                    x={leftX}
                    y={paddingTop}
                    width={Math.max(rightX - leftX, 8)}
                    height={height - paddingTop - paddingBottom}
                    fill="transparent"
                    onMouseEnter={() => { if (!isDraggingRef.current) setHoverIndex(index); }}
                    onMouseMove={() => { if (!isDraggingRef.current) setHoverIndex(index); }}
                  />
                );
              })}
            </svg>

            {hoverPoint && (
              <div
                style={{
                  ...tooltipStyle,
                  left: `${Math.min(Math.max((tooltipX / width) * 100, 10), 82)}%`,
                  top: `${Math.min(Math.max((tooltipY / height) * 100, 14), 72)}%`,
                }}
              >
                <div style={tooltipDateStyle}>{formatDateLabel(hoverPoint.parsedDate, range)}</div>
                <div style={tooltipPriceStyle}>{formatCurrency(hoverPoint.price, instrumentCurrency)}</div>
                {hoverAvgBuy !== null && (
                  <div style={tooltipSubStyle}>Avg buy: {formatCurrency(hoverAvgBuy, instrumentCurrency)}</div>
                )}
                {hoverAvgBuy !== null && hoverAvgBuy > 0 && (
                  <div style={{ ...tooltipSubStyle, color: hoverPoint.price >= hoverAvgBuy ? 'var(--success)' : 'var(--danger)' }}>
                    {hoverPoint.price >= hoverAvgBuy ? '+' : ''}
                    {formatCurrency(hoverPoint.price - hoverAvgBuy, instrumentCurrency)}{' '}
                    ({(((hoverPoint.price - hoverAvgBuy) / hoverAvgBuy) * 100).toFixed(2)}%)
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function buildYTicks(min, max, targetCount = 12) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];
  const range = max - min;
  const roughStep = range / (targetCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const norm = roughStep / magnitude;
  let niceStep;
  if (norm <= 1.5) niceStep = 1;
  else if (norm <= 3) niceStep = 2;
  else if (norm <= 4) niceStep = 2.5;
  else if (norm <= 7.5) niceStep = 5;
  else niceStep = 10;
  niceStep *= magnitude;

  const firstTick = Math.ceil(min / niceStep) * niceStep;
  const ticks = [];
  for (let t = firstTick; t <= max + niceStep * 0.01; t += niceStep) {
    ticks.push(Math.abs(Math.round(t * 1e9) / 1e9) < 1e-9 ? 0 : Math.round(t * 1e9) / 1e9);
    if (ticks.length >= 14) break;
  }
  return ticks;
}

function buildXTickIndexes(length) {
  if (length <= 1) return [0];
  if (length <= 4) return [0, length - 1];
  // 3 ticks: left, middle, right
  return [0, Math.round((length - 1) / 2), length - 1];
}

function formatAxisNumber(value, currency) {
  const abs = Math.abs(value);
  let v = value, suffix = '';
  if (abs >= 1_000_000) { v = value / 1_000_000; suffix = 'M'; }
  else if (abs >= 1_000) { v = value / 1_000; suffix = 'k'; }
  const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: abs >= 1000 ? 1 : 2 }).format(v);
  try {
    const sym = new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).formatToParts(0).find((p) => p.type === 'currency')?.value || '';
    return `${sym}${formatted}${suffix}`;
  } catch {
    return `${formatted}${suffix}`;
  }
}

function formatXLabel(date, range, visibleSpanDays = 0) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  // Intraday — always show time only
  if (range === '1d') return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' }).format(date);
  // 7d (30-min bars) — show day + hour
  if (range === '7d') return new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', hour: '2-digit' }).format(date);
  // For all other ranges use visible span to pick granularity, regardless of selected range
  // so zooming in on a 5Y chart still shows day+month when the window is narrow
  if (visibleSpanDays > 0 && visibleSpanDays <= 90)
    return new Intl.DateTimeFormat('en-US', { day: '2-digit', month: '2-digit' }).format(date);
  if (visibleSpanDays > 0 && visibleSpanDays <= 365)
    return new Intl.DateTimeFormat('en-US', { day: '2-digit', month: '2-digit' }).format(date);
  // Wide span → month/year
  return new Intl.DateTimeFormat('en-US', { month: '2-digit', year: '2-digit' }).format(date);
}

function formatDateLabel(date, range) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '-';
  // For intraday ranges show full date + time in tooltip
  if (range === '1d') return new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' }).format(date);
  if (range === '7d') return new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(date);
  return new Intl.DateTimeFormat('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

function formatCurrency(value, currency) {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2 }).format(Number(value || 0));
  } catch {
    return Number(value || 0).toFixed(2) + ' ' + (currency || '');
  }
}

const cardStyle = { background: 'var(--surface-strong)', borderRadius: '18px', padding: '20px 24px', paddingBottom: 0, boxShadow: 'var(--shadow)', border: '1px solid var(--border)', minWidth: 0, width: '100%', boxSizing: 'border-box', overflow: 'hidden' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' };
const titleStyle = { margin: 0, color: 'var(--text-primary)', fontSize: '22px', fontWeight: 800 };
const subtitleStyle = { marginTop: '4px', color: 'var(--text-secondary)', fontSize: '13px' };
const rightHeaderStyle = { display: 'flex', gap: '18px', alignItems: 'stretch', flexWrap: 'wrap' };

const valueValueStyle = { color: 'var(--text-primary)', fontSize: '20px', fontWeight: 800, textAlign: 'right' };
const plValueStyle = { fontSize: '22px', fontWeight: 800, textAlign: 'right' };
const plSubValueStyle = { fontSize: '13px', fontWeight: 700, textAlign: 'right' };
const rangeBarStyle = { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' };
const rangeButtonStyle = { border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', borderRadius: '999px', padding: '7px 12px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
const activeRangeButtonStyle = { ...rangeButtonStyle, background: 'var(--primary)', color: '#fff', border: '1px solid var(--primary)' };
const legendStyle = { display: 'flex', flexWrap: 'wrap', gap: '14px', marginBottom: '10px' };
const legendItemStyle = { display: 'inline-flex', alignItems: 'center', gap: '7px', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600 };
const legendSwatchStyle = { width: '12px', height: '12px', borderRadius: '999px', display: 'inline-block' };
const legendLineStyle = { width: '18px', height: '0', borderTopWidth: '2px', borderTopStyle: 'dashed', display: 'inline-block' };
const legendToggleStyle = { display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'opacity 0.15s' };
const buyLineLegendStyle = { display: 'inline-block', width: '2px', height: '14px', borderLeft: '2px dashed var(--success)', flexShrink: 0 };
const chartWrapperStyle = { marginLeft: '-24px', marginRight: '-24px', height: '340px', position: 'relative' };
const svgStyle = { width: '100%', height: '100%', display: 'block' };
const tooltipStyle = { position: 'absolute', transform: 'translate(-50%, -110%)', pointerEvents: 'none', background: 'rgba(15, 23, 42, 0.96)', color: '#fff', padding: '10px 12px', borderRadius: '12px', minWidth: '170px', boxShadow: '0 10px 30px rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.08)' };
const tooltipDateStyle = { fontSize: '11px', opacity: 0.8, marginBottom: '6px' };
const tooltipPriceStyle = { fontSize: '15px', fontWeight: 800, marginBottom: '4px' };
const tooltipSubStyle = { fontSize: '12px', opacity: 0.85 };
const emptyTextStyle = { margin: 0, color: 'var(--text-secondary)', padding: '20px 0' };
const intradayEmptyStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '32px 0', color: 'var(--text-secondary)', fontSize: '14px', textAlign: 'center' };
const intradayFallbackBtnStyle = { background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 18px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' };

const mobileControlsRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
};

const mobileDropdownBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  borderRadius: '999px',
  padding: '8px 14px',
  fontSize: '13px',
  fontWeight: 700,
  cursor: 'pointer',
};

const mobileDropdownPanelStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  background: 'var(--surface-strong)',
  border: '1px solid var(--border)',
  borderRadius: '14px',
  boxShadow: 'var(--shadow)',
  zIndex: 30,
  minWidth: '160px',
  padding: '6px',
  display: 'grid',
  gap: '2px',
};

const dropdownItemStyle = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '9px 12px',
  borderRadius: '9px',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '13px',
  textAlign: 'left',
  gap: '6px',
};

const dropdownItemActiveStyle = {
  background: 'var(--primary)',
  color: '#ffffff',
};

export default HoldingChart;
